class Iphone18ProductStory extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.cacheElements();

    if (!this.hasRequiredElements()) return;

    this.itemCount = this.copies.length;
    this.current = 0;
    this.timeline = null;
    this.scrollTrigger = null;
    this.mobileAnimationFrame = null;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handlePagerClick = this.handlePagerClick.bind(this);
    this.handleWindowLoad = this.handleWindowLoad.bind(this);

    this.addEventListener('click', this.handlePagerClick);
    this.paint(0);

    this.gsap = window.gsap;
    this.ScrollTrigger = window.ScrollTrigger;

    if (!this.gsap || !this.ScrollTrigger || this.itemCount < 2) return;

    this.gsap.registerPlugin(this.ScrollTrigger);

    // 移动端浏览器地址栏收起会改变视口高度并触发 resize -> refresh。
    // 这里只关心宽度，忽略纯高度变化，避免滚动时反复重算 pin。
    this.ScrollTrigger.config({ ignoreMobileResize: true });

    this.gsapMatchMedia = this.gsap.matchMedia();

    // 断点只管布局，动效偏好在 goTo 里单独处理，两者不要混进同一个查询
    this.gsapMatchMedia.add('(min-width: 769px)', () => this.setupDesktop());
    this.gsapMatchMedia.add('(max-width: 768px)', () => this.setupCards());

    this.classList.add('is-enhanced');

    if (document.readyState === 'complete') {
      this.ScrollTrigger.refresh();
    } else {
      window.addEventListener('load', this.handleWindowLoad, { once: true });
    }
  }

  cacheElements() {
    this.storySection = this.querySelector('[data-story-section]');
    // pin 只作用在桌面端专属的这一层，绝不碰共用的 section
    this.desktopRoot = this.querySelector('[data-desktop-root]');
    this.mobileRoot = this.querySelector('[data-mobile-root]');
    this.visual = this.querySelector('[data-visual]');
    this.pager = this.querySelector('[data-pager]');
    this.cardsBox = this.querySelector('[data-cards]');
    this.track = this.querySelector('[data-track]');
    this.idxLabel = this.querySelector('[data-idx-2]');
    this.idxCount = this.querySelector('[data-idx-1]');
    this.liveRegion = this.querySelector('[data-story-live]');
    this.copies = [...this.querySelectorAll('[data-copy]')];
    // 光晕 / 序号 / 产品 / 倒影都烘焙在整图里，视觉层只剩这一组元素
    this.scenes = this.visual ? [...this.visual.querySelectorAll('[data-scene]')] : [];
    // 桌面和手机各有一套分页，用 data-story-index 认索引而不是数组下标
    this.dots = [...this.querySelectorAll('[data-story-dot]')];
    this.copyActions = this.copies.map((copy) => [...copy.querySelectorAll('a, button')]);
    this.copyItems = this.copies.map((copy) => [...copy.querySelectorAll('[data-anim]')]);
  }

  hasRequiredElements() {
    const itemCount = this.copies.length;

    return Boolean(
      this.storySection &&
      this.desktopRoot &&
      this.mobileRoot &&
      this.visual &&
      this.pager &&
      this.cardsBox &&
      this.track &&
      this.idxLabel &&
      this.idxCount &&
      itemCount > 0 &&
      this.scenes.length === itemCount &&
      this.dots.length >= itemCount &&
      this.dots.length % itemCount === 0 &&
      this.track.children.length === itemCount
    );
  }

  pad(index) {
    return String(index + 1).padStart(2, '0');
  }

  /** 所有被 GSAP 动过的元素，用于统一清理 */
  animatedElements() {
    return [...this.copyItems.flat(), ...this.scenes];
  }

  /**
   * 只处理属性和索引。内联样式交给 ScrollTrigger.saveStyles() 还原，
   * 不要在这里 clearProps —— 那会清成 CSS 默认值，而不是进入断点前的状态。
   */
  resetState() {
    if (this.timeline) {
      this.timeline.kill();
      this.timeline = null;
    }

    this.copies.forEach((copy, copyIndex) => {
      copy.setAttribute('aria-hidden', copyIndex === 0 ? 'false' : 'true');
    });

    this.current = 0;
    this.paint(0);
  }

  paint(index) {
    this.dots.forEach((dot) => {
      const dotIndex = Number(dot.dataset.storyIndex);
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });
    this.idxLabel.textContent = this.pad(index);
    this.idxCount.textContent = String(index + 1);

    this.copyActions.forEach((actions, copyIndex) => {
      actions.forEach((action) => {
        if (copyIndex === index) {
          action.removeAttribute('tabindex');
        } else {
          action.setAttribute('tabindex', '-1');
        }
      });
    });

    if (this.liveRegion) {
      const title = this.copies[index].dataset.storyTitle || '';
      this.liveRegion.textContent = `${index + 1} / ${this.itemCount} ${title}`.trim();
    }
  }

  /** 减弱动效时的瞬时切换，不走 timeline */
  jumpTo(index) {
    this.copies.forEach((copy, copyIndex) => {
      const isActive = copyIndex === index;

      copy.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      this.gsap.set(this.copyItems[copyIndex], { opacity: isActive ? 1 : 0, y: 0 });
      this.gsap.set(this.scenes[copyIndex], { opacity: isActive ? 1 : 0, scale: 1 });
    });
  }

  goTo(index) {
    if (index === this.current || index < 0 || index > this.itemCount - 1) return;

    const previous = this.current;
    const direction = index > previous ? 1 : -1;
    const interrupted = Boolean(this.timeline && this.timeline.isActive());
    this.current = index;
    this.paint(index);

    if (this.motionQuery.matches) {
      this.jumpTo(index);
      return;
    }

    if (this.timeline) this.timeline.kill();
    this.timeline = this.gsap.timeline({ defaults: { overwrite: true } });

    this.copies[index].setAttribute('aria-hidden', 'false');

    for (let panelIndex = 0; panelIndex < this.itemCount; panelIndex += 1) {
      if (panelIndex === index) continue;

      const duration = panelIndex === previous && !interrupted ? 0.28 : 0.14;

      this.timeline
        .to(
          this.copyItems[panelIndex],
          {
            opacity: 0,
            y: -12 * direction,
            duration,
            stagger: duration > 0.2 ? 0.02 : 0,
            ease: 'power2.in'
          },
          0
        )
        .to(
          this.scenes[panelIndex],
          {
            opacity: 0,
            scale: 0.96,
            duration: duration * 1.3,
            ease: 'power2.in'
          },
          0
        )
        .set(this.copies[panelIndex], { attr: { 'aria-hidden': 'true' } }, duration);
    }

    const enterAt = interrupted ? 0.06 : 0.18;

    this.timeline
      .fromTo(
        this.copyItems[index],
        { opacity: 0, y: 16 * direction },
        {
          opacity: 1,
          y: 0,
          duration: 0.55,
          stagger: 0.05,
          ease: 'power3.out'
        },
        enterAt
      )
      .fromTo(
        this.scenes[index],
        { opacity: 0, scale: 1.04 },
        { opacity: 1, scale: 1, duration: 0.65, ease: 'power3.out' },
        enterAt
      );
  }

  handlePagerClick(event) {
    const dot = event.target.closest('[data-story-dot]');
    if (!dot || !this.contains(dot)) return;

    const index = Number(dot.dataset.storyIndex);
    if (!Number.isInteger(index) || index < 0 || index > this.itemCount - 1) return;

    if (this.scrollTrigger && this.itemCount > 1) {
      const top =
        this.scrollTrigger.start +
        (this.scrollTrigger.end - this.scrollTrigger.start) * (index / (this.itemCount - 1));
      window.scrollTo({ top, behavior: this.motionQuery.matches ? 'auto' : 'smooth' });
      return;
    }

    const card = this.track.children[index];
    if (card) {
      card.scrollIntoView({
        behavior: this.motionQuery.matches ? 'auto' : 'smooth',
        inline: 'start',
        block: 'nearest'
      });
    }
  }

  setupDesktop() {
    // 先归零。手机端横滑会把 current 改成 2 或 3，
    // 若不重置，切回 PC 后 goTo(2) 会被 index === current 早退吞掉，
    // 结果画面停在第 1 屏而顶栏/分页显示 03。
    this.resetState();

    // 登记这批元素当前的内联样式。断点失配时 GSAP 会逐个还原，
    // 包括 goTo 的 timeline 写上去的 opacity / transform —— 那些 timeline
    // 建在 ScrollTrigger 回调里，不受 context 追踪，只有 saveStyles 兜得住。
    // 必须在任何动画之前调用。
    this.ScrollTrigger.saveStyles(this.animatedElements());

    this.gsap.set(this.scenes[0], { opacity: 1 });

    this.scrollTrigger = this.ScrollTrigger.create({
      trigger: this.desktopRoot,
      pin: this.desktopRoot,
      start: 'top top',
      end: () => `+=${window.innerHeight * (this.itemCount - 1)}`,
      pinSpacing: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      snap: {
        snapTo: 1 / (this.itemCount - 1),
        duration: { min: 0.2, max: 0.5 },
        delay: 0.08,
        ease: 'power1.inOut'
      },
      onUpdate: (self) => this.goTo(Math.round(self.progress * (this.itemCount - 1)))
    });

    return () => {
      // 不要在这里 kill 或 refresh。
      // ScrollTrigger 建在 matchMedia 回调里，matchMedia 会自己 revert 它、
      // 拆掉 pin-spacer；而 revert 本身就跑在一次 refresh 周期内，
      // 在里面再触发 refresh 会递归，pin 的还原只做一半。
      this.scrollTrigger = null;
      this.resetState();
    };
  }

  setupCards() {
    this.cardsBox.scrollLeft = 0;
    this.resetState();

    this.handleCardsScroll = () => {
      if (this.mobileAnimationFrame) return;

      this.mobileAnimationFrame = requestAnimationFrame(() => {
        this.mobileAnimationFrame = null;
        const boxLeft = this.cardsBox.getBoundingClientRect().left;
        let best = 0;
        let bestDistance = Infinity;

        [...this.track.children].forEach((card, cardIndex) => {
          const distance = Math.abs(card.getBoundingClientRect().left - boxLeft);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = cardIndex;
          }
        });

        if (best !== this.current) {
          this.current = best;
          this.paint(best);
        }
      });
    };

    this.cardsBox.addEventListener('scroll', this.handleCardsScroll, { passive: true });

    return () => {
      this.cardsBox.removeEventListener('scroll', this.handleCardsScroll);
      if (this.mobileAnimationFrame) cancelAnimationFrame(this.mobileAnimationFrame);
      this.mobileAnimationFrame = null;
      this.handleCardsScroll = null;
      this.cardsBox.scrollLeft = 0;
    };
  }

  handleWindowLoad() {
    this.ScrollTrigger.refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handlePagerClick);
    window.removeEventListener('load', this.handleWindowLoad);

    if (this.handleCardsScroll) {
      this.cardsBox?.removeEventListener('scroll', this.handleCardsScroll);
    }
    if (this.mobileAnimationFrame) cancelAnimationFrame(this.mobileAnimationFrame);

    this.timeline?.kill();

    // revert() 会一并还原 saveStyles 登记的样式、kill 掉 ScrollTrigger、拆掉 pin
    this.gsapMatchMedia?.revert();

    this.classList.remove('is-enhanced');

    this.mobileAnimationFrame = null;
    this.handleCardsScroll = null;
    this.timeline = null;
    this.scrollTrigger = null;
    this.gsapMatchMedia = null;
    this.isInitialized = false;
  }
}

if (!customElements.get('iphone-18-product-story')) {
  customElements.define('iphone-18-product-story', Iphone18ProductStory);
}